import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import { Link, Redirect, useParams } from "react-router-dom";
import { TbArrowLeft, TbBuildingBank, TbChevronRight, TbLock, TbWallet } from "react-icons/tb";
import { CurrencyIcon } from "../../../../Components/Atoms/CurrencyIcon";
import { CrossborderCopyButton } from "../../Crossborder/components/CrossborderCopyButton";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import {
  fetchBaasStablesWalletById,
} from "../../../../Services/baasStablesWalletApi";
import { BaasStablesWalletDetailSkeleton } from "../components/BaasSkeleton";
import { BAAS_STABLES_WALLET_PATH, buildBaasAccountDetailPath } from "../constants";
import { formatStable } from "../../Stables/constants";
import { truncateBaasStablesAddress } from "./baasStablesExplorer";
import { BaasStablesNetworkBadge } from "./components/BaasStablesNetworkBadge";
import { BaasStablesRecentActivity } from "./components/BaasStablesRecentActivity";
import { BaasStablesWalletQuickActions } from "./components/BaasStablesWalletQuickActions";
import { BaasStablesWalletStatusBadge } from "./components/BaasStablesWalletStatusBadge";

const DETAIL_TABS = [
  { id: "details", labelKey: "baas.stablesWallet.detail.tabs.details" },
  { id: "metadata", labelKey: "baas.stablesWallet.detail.tabs.metadata" },
];

const METADATA_FIELDS = [
  { key: "blockchainWalletId", labelKey: "baas.stablesWallet.metadata.fields.blockchainWalletId" },
  { key: "providerRef", labelKey: "baas.stablesWallet.metadata.fields.providerRef" },
  { key: "contractAddress", labelKey: "baas.stablesWallet.metadata.fields.contractAddress" },
];

const ASSET_CHIP_STYLES = {
  USDC: "border-[#e7a73f]/45 bg-[#e7a73f]/10 text-[#a66f1d] dark:border-brand-gold-mid/50 dark:bg-brand-gold-mid/15 dark:text-brand-gold-mid",
  USDT: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300",
  EURC: "border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-900/60 dark:bg-indigo-950/40 dark:text-indigo-300",
  BRL1: "border-green-200 bg-green-50 text-green-800 dark:border-green-900/60 dark:bg-green-950/40 dark:text-green-300",
};

const BORDER_CARD =
  "rounded-xl border border-gray-200 bg-white dark:border-brand-dark-border dark:bg-brand-dark-surface";

const displaySensitiveValue = (value, visible) => {
  if (value == null || value === "") return value;
  if (visible) return value;
  return truncateBaasStablesAddress(String(value));
};

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
};

/** Wallet amounts only — never formatCurrency / fiat BRL (BRL1 ≠ conta BRL). */
const formatStableAmount = (value, asset) =>
  `${formatStable(value ?? 0)}${asset ? ` ${asset}` : ""}`.trim();

const BaasStablesAssetChip = ({ asset, iconUrl }) => {
  const chipStyle =
    ASSET_CHIP_STYLES[asset] ??
    "border-gray-200 bg-gray-50 text-gray-700 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-200";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${chipStyle}`}
    >
      <CurrencyIcon asset={asset?.toLowerCase()} iconUrl={iconUrl} size={16} />
      {asset}
    </span>
  );
};

const DetailRow = ({ label, value, mono = false, copyValue }) => {
  if (value == null || value === "") return null;

  return (
    <div
      className={`flex min-w-0 flex-col gap-0.5 border-b border-gray-100 py-2 last:border-b-0 dark:border-brand-dark-border ${
        mono ? "" : "sm:flex-row sm:items-start sm:justify-between sm:gap-3"
      }`}
    >
      <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <div className={`flex min-w-0 items-start gap-1 ${mono ? "w-full" : "sm:justify-end"}`}>
        <span
          className={`min-w-0 text-sm font-medium text-brand-dark dark:text-gray-100 ${
            mono
              ? "w-full break-all font-mono text-xs leading-relaxed"
              : "sm:text-right"
          }`}
        >
          {value}
        </span>
        {copyValue ? (
          <CrossborderCopyButton
            value={copyValue}
            ariaLabel={intl.get("baas.stablesWallet.metadata.copyValue", { field: label })}
          />
        ) : null}
      </div>
    </div>
  );
};

const BalanceKpiCard = ({ icon: Icon, title, value }) => (
  <div className="summary-card !p-3">
    <div className="flex min-w-0 items-center gap-2.5">
      <div className="summary-card-icon !h-7 !w-7">
        <Icon className="h-3.5 w-3.5 text-[#e7a73f]" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">{title}</p>
        <p className="mt-0.5 break-words text-base font-bold leading-tight text-brand-dark dark:text-gray-100 sm:text-lg">
          {value}
        </p>
      </div>
    </div>
  </div>
);

const WalletBalanceKpiCards = ({ wallet }) => {
  const { asset } = wallet;
  const balances = wallet.balances ?? {};

  return (
    <section>
      <p className="dashboard-section-label mb-2">
        {intl.get("baas.stablesWallet.detail.balancesTitle")}
      </p>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
        <BalanceKpiCard
          icon={TbWallet}
          title={intl.get("baas.processing.available")}
          value={formatStableAmount(balances.available, asset)}
        />
        <BalanceKpiCard
          icon={TbLock}
          title={intl.get("baas.processing.blocked")}
          value={formatStableAmount(balances.blocked, asset)}
        />
        <BalanceKpiCard
          icon={TbWallet}
          title={intl.get("baas.processing.total")}
          value={formatStableAmount(balances.total, asset)}
        />
      </div>
    </section>
  );
};

const WalletInfoGrid = ({ wallet, sensitiveVisible }) => (
  <section className="flex h-full min-w-0 flex-col">
    <p className="dashboard-section-label mb-2">
      {intl.get("baas.stablesWallet.detail.walletInfo")}
    </p>
    <div className={`${BORDER_CARD} flex flex-1 flex-col px-3 sm:px-4`}>
      <div className="grid min-w-0 gap-x-4 sm:grid-cols-2">
        <DetailRow label={intl.get("baas.stablesWallet.columns.entity")} value={wallet.entityName} />
        <DetailRow
          label={intl.get("baas.stablesWallet.detail.entityId")}
          value={displaySensitiveValue(wallet.entityId, sensitiveVisible)}
          mono
          copyValue={wallet.entityId}
        />
        <DetailRow
          label={intl.get("baas.stablesWallet.columns.network")}
          value={<BaasStablesNetworkBadge network={wallet.network} />}
        />
        <DetailRow
          label={intl.get("baas.stablesWallet.columns.asset")}
          value={<BaasStablesAssetChip asset={wallet.asset} iconUrl={wallet.iconUrl} />}
        />
        <DetailRow
          label={intl.get("baas.stablesWallet.columns.status")}
          value={<BaasStablesWalletStatusBadge status={wallet.status} />}
        />
        <DetailRow
          label={intl.get("baas.stablesWallet.detail.createdAt")}
          value={formatDateTime(wallet.createdAt)}
        />
        <DetailRow
          label={intl.get("baas.stablesWallet.detail.balanceId")}
          value={displaySensitiveValue(wallet.balanceId || wallet.id, sensitiveVisible)}
          mono
          copyValue={wallet.balanceId || wallet.id}
        />
        {wallet.apiWalletId ? (
          <DetailRow
            label={intl.get("baas.stablesWallet.detail.walletId")}
            value={displaySensitiveValue(wallet.apiWalletId, sensitiveVisible)}
            mono
            copyValue={wallet.apiWalletId}
          />
        ) : null}
      </div>
    </div>
  </section>
);

const LinkedAccountCard = ({ wallet, sensitiveVisible }) => {
  const alias = wallet.accountAlias?.trim() || "";
  const accountNumber = wallet.accountNumber?.trim() || "";
  const showDistinctAlias = Boolean(alias) && alias !== accountNumber;
  const primaryValue = showDistinctAlias ? alias : accountNumber || alias || "—";
  const primaryLabelKey = showDistinctAlias
    ? "baas.stablesWallet.detail.accountAlias"
    : "baas.stablesWallet.detail.accountNumber";
  // When alias equals number, primary already shows it — avoid a second identical row.
  const showAccountNumberRow = Boolean(accountNumber) && showDistinctAlias;

  return (
    <section className="flex h-full min-w-0 flex-col">
      <p className="dashboard-section-label mb-2">
        {intl.get("baas.stablesWallet.detail.linkedAccountTitle")}
      </p>
      <div className={`${BORDER_CARD} flex flex-1 flex-col p-3 sm:p-3.5`}>
        <div className="flex min-w-0 items-start gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#e7a73f]/10 text-[#e7a73f]">
            <TbBuildingBank className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <div className="min-w-0">
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {intl.get(primaryLabelKey)}
              </p>
              <div className="mt-0.5 flex min-w-0 items-start gap-1">
                {wallet.accountId ? (
                  <Link
                    to={buildBaasAccountDetailPath(wallet.accountId)}
                    className="inline-flex min-w-0 items-center gap-1 break-words text-sm font-semibold text-[#e7a73f] transition hover:underline"
                  >
                    {primaryValue}
                    <TbChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  </Link>
                ) : (
                  <p className="break-words text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {primaryValue}
                  </p>
                )}
                {!showDistinctAlias && accountNumber ? (
                  <CrossborderCopyButton
                    value={accountNumber}
                    ariaLabel={intl.get("baas.stablesWallet.metadata.copyValue", {
                      field: intl.get("baas.stablesWallet.detail.accountNumber"),
                    })}
                  />
                ) : null}
              </div>
            </div>
            {(showAccountNumberRow || wallet.accountId) ? (
              <div className="flex min-w-0 flex-col gap-2 border-t border-gray-100 pt-2 dark:border-brand-dark-border">
                {showAccountNumberRow ? (
                  <div className="min-w-0">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {intl.get("baas.stablesWallet.detail.accountNumber")}
                    </p>
                    <div className="mt-0.5 flex min-w-0 items-start gap-1">
                      <span className="min-w-0 break-all font-mono text-xs leading-relaxed text-brand-dark dark:text-gray-100">
                        {accountNumber}
                      </span>
                      <CrossborderCopyButton
                        value={accountNumber}
                        ariaLabel={intl.get("baas.stablesWallet.metadata.copyValue", {
                          field: intl.get("baas.stablesWallet.detail.accountNumber"),
                        })}
                      />
                    </div>
                  </div>
                ) : null}
                {wallet.accountId ? (
                  <div className="min-w-0">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {intl.get("baas.stablesWallet.detail.accountId")}
                    </p>
                    <div className="mt-0.5 flex min-w-0 items-start gap-1">
                      <span className="min-w-0 break-all font-mono text-xs leading-relaxed text-brand-dark dark:text-gray-100">
                        {displaySensitiveValue(wallet.accountId, sensitiveVisible)}
                      </span>
                      <CrossborderCopyButton
                        value={wallet.accountId}
                        ariaLabel={intl.get("baas.stablesWallet.metadata.copyValue", {
                          field: intl.get("baas.stablesWallet.detail.accountId"),
                        })}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

const DetailsPanel = ({ wallet, sensitiveVisible }) => (
  <div className="space-y-4">
    <WalletBalanceKpiCards wallet={wallet} />

    <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-2">
      <WalletInfoGrid wallet={wallet} sensitiveVisible={sensitiveVisible} />
      <LinkedAccountCard wallet={wallet} sensitiveVisible={sensitiveVisible} />
    </div>

    <BaasStablesRecentActivity
      operations={wallet.recentOperations ?? []}
      maxRows={6}
      walletId={wallet.id}
    />
  </div>
);

const MetadataPanel = ({ wallet, sensitiveVisible }) => {
  const metadata = wallet.metadata ?? {};
  const hasMetadata = METADATA_FIELDS.some((field) => metadata[field.key]);

  return (
    <section>
      <p className="dashboard-section-label mb-2">
        {intl.get("baas.stablesWallet.metadata.title")}
      </p>
      {hasMetadata ? (
        <div className={`${BORDER_CARD} px-3 sm:px-4`}>
          {METADATA_FIELDS.map((field) => (
            <DetailRow
              key={field.key}
              label={intl.get(field.labelKey)}
              value={displaySensitiveValue(metadata[field.key], sensitiveVisible)}
              mono
              copyValue={metadata[field.key] || undefined}
            />
          ))}
        </div>
      ) : (
        <div className={`${BORDER_CARD} px-3 py-2.5`}>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.stablesWallet.metadata.empty")}
          </p>
        </div>
      )}
    </section>
  );
};

export const BaasStablesWalletDetailPage = () => {
  const { walletId } = useParams();
  const { selectedInstance } = useBaasInstance();
  const isLoading = useBaasPageLoading(walletId);
  const { can } = useBaasCapabilities();
  const allowed = can("processing.stables.view");
  const canOperate = can("stables.operate");
  const [activeTab, setActiveTab] = useState("details");
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [sensitiveVisible, setSensitiveVisible] = useState(true);

  const toggleSensitive = () => setSensitiveVisible((prev) => !prev);

  useEffect(() => {
    setActiveTab("details");
    setSensitiveVisible(true);
  }, [walletId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance || !walletId) {
        setWallet(null);
        setWalletLoading(false);
        return;
      }

      setWalletLoading(true);
      try {
        const next = await fetchBaasStablesWalletById(selectedInstance, walletId);
        if (!cancelled) setWallet(next);
      } catch {
        if (!cancelled) setWallet(null);
      } finally {
        if (!cancelled) setWalletLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, walletId]);

  const refreshWallet = useCallback(() => {
    if (!selectedInstance || !walletId) return;
    void (async () => {
      try {
        const next = await fetchBaasStablesWalletById(selectedInstance, walletId);
        setWallet(next);
      } catch {
        // Keep current wallet on soft refresh failure.
      }
    })();
  }, [selectedInstance, walletId]);

  if (isLoading || walletLoading) {
    return <BaasStablesWalletDetailSkeleton />;
  }

  if (!allowed) {
    return <Redirect to={BAAS_STABLES_WALLET_PATH} />;
  }

  if (!wallet) {
    return <Redirect to={BAAS_STABLES_WALLET_PATH} />;
  }

  const headerAddress = displaySensitiveValue(wallet.address, sensitiveVisible);

  return (
    <div className="baas-stables-wallet min-w-0 space-y-4">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#e7a73f]">
            {intl.get("baas.stablesWallet.detail.eyebrow")}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2.5">
            <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">{wallet.alias}</h1>
            <BaasStablesWalletStatusBadge status={wallet.status} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <BaasStablesAssetChip asset={wallet.asset} iconUrl={wallet.iconUrl} />
            <BaasStablesNetworkBadge network={wallet.network} />
            <span className="inline-flex max-w-full items-start gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-600 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-300">
              <span className="min-w-0 break-all leading-relaxed">{headerAddress}</span>
              <CrossborderCopyButton
                value={wallet.address}
                ariaLabel={intl.get("baas.stablesWallet.detail.copyAddress")}
              />
            </span>
          </div>
          <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{wallet.entityName}</p>
        </div>

        <Link to={BAAS_STABLES_WALLET_PATH} className="btn-outline w-auto shrink-0 self-start !text-xs">
          <TbArrowLeft className="h-4 w-4" />
          {intl.get("baas.stablesWallet.detail.back")}
        </Link>
      </header>

      <BaasStablesWalletQuickActions
        walletId={wallet.id}
        address={wallet.address}
        network={wallet.network}
        disabled={!canOperate}
        sensitiveVisible={sensitiveVisible}
        onToggleSensitive={toggleSensitive}
        accountId={wallet.accountId}
        walletStatus={wallet.status}
        onLifecycleChanged={refreshWallet}
      />

      <div
        className="extract-view-tabs extract-view-tabs--inline border-b border-gray-100 dark:border-brand-dark-border"
        role="tablist"
        aria-label={intl.get("baas.stablesWallet.detail.title")}
      >
        {DETAIL_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`extract-view-tab ${isActive ? "extract-view-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {intl.get(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {activeTab === "details" ? (
        <DetailsPanel wallet={wallet} sensitiveVisible={sensitiveVisible} />
      ) : (
        <MetadataPanel wallet={wallet} sensitiveVisible={sensitiveVisible} />
      )}
    </div>
  );
};
